import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Calendar, MapPin, Clock, Star, Loader2, Package, Building2, DollarSign, XCircle, Eye, Phone, Users, CreditCard } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { formatDateUK, formatCurrency } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const MyBookings = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [equipmentBookings, setEquipmentBookings] = useState([]);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [ratingBooking, setRatingBooking] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [ratingError, setRatingError] = useState('');
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [paying, setPaying] = useState(false);
  const [activeTab, setActiveTab] = useState('systems');

  useEffect(() => {
    fetchAllBookings();
  }, []);

  const fetchAllBookings = async () => {
    if (!BACKEND_URL) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [systemsRes, equipmentRes] = await Promise.all([
        axios.get(`${API}/bookings`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/equipment-bookings`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setBookings(Array.isArray(systemsRes?.data) ? systemsRes.data : []);
      setEquipmentBookings(Array.isArray(equipmentRes?.data) ? equipmentRes.data : []);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const openRatingModal = (booking) => {
    setRatingBooking(booking);
    setRating(0);
    setHoverRating(0);
    setRatingComment('');
    setRatingError('');
    setShowRatingModal(true);
  };

  const openDetailsModal = (booking) => {
    setSelectedBooking(booking);
    setShowDetailsModal(true);
  };

  const handleCancelBooking = async (booking, isEquipment = false) => {
    const isPaid = booking?.payment_status === 'paid';
    const amount = booking?.total_amount || 0;
    const cancellationFee = isPaid ? (amount * 0.20).toFixed(2) : 0;
    const refundAmount = isPaid ? (amount * 0.80).toFixed(2) : 0;
    
    let confirmMessage = 'Are you sure you want to cancel this booking?';
    if (isPaid) {
      confirmMessage = `Are you sure you want to cancel this booking?\n\n` +
        `⚠️ CANCELLATION POLICY:\n` +
        `• Original Payment: R${amount.toFixed(2)}\n` +
        `• Cancellation Fee (20%): R${cancellationFee}\n` +
        `• Refund Amount: R${refundAmount}\n\n` +
        `The refund will be processed within 5-7 business days.`;
    }
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setCancelling(true);
    try {
      const endpoint = isEquipment 
        ? `${API}/equipment-bookings/${booking.booking_id}`
        : `${API}/bookings/${booking.booking_id}`;
      
      const response = await axios.delete(endpoint, { withCredentials: true });
      
      // Show refund information if applicable
      if (response.data.refund) {
        alert(`Booking cancelled.\n\n${response.data.refund.message}`);
      }
      
      fetchAllBookings();
      setShowDetailsModal(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  const canCancel = (booking) => {
    return booking?.status === 'pending' || booking?.status === 'confirmed';
  };

  const canPay = (booking) => {
    return booking?.payment_status === 'pending' && booking?.status !== 'cancelled';
  };

  const handlePayNow = async (booking, isEquipment = false) => {
    setPaying(true);
    try {
      const response = await axios.post(`${API}/payments/initiate`, {
        booking_id: booking.booking_id,
        booking_type: isEquipment ? 'equipment' : 'pa_system'
      }, { withCredentials: true });

      if (response.data.payment_url) {
        // Redirect to PayFast
        window.location.href = response.data.payment_url;
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to initiate payment');
      setPaying(false);
    }
  };

  const handleSubmitRating = async () => {
    if (rating === 0) {
      setRatingError('Please select a rating');
      return;
    }

    setSubmittingRating(true);
    setRatingError('');

    try {
      await axios.post(`${API}/bookings/${ratingBooking.booking_id}/rate-business`, {
        rating,
        comment: ratingComment || null
      }, { withCredentials: true });

      setShowRatingModal(false);
      fetchAllBookings();
    } catch (error) {
      setRatingError(error.response?.data?.detail || 'Failed to submit rating');
    } finally {
      setSubmittingRating(false);
    }
  };

  const canRate = (booking) => {
    return ['completed', 'confirmed', 'paid'].includes(booking?.status) && !booking?.business_rating && !booking?.rating;
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'confirmed': return 'bg-green-500';
      case 'pending': return 'bg-yellow-500';
      case 'cancelled': return 'bg-red-500';
      case 'completed': return 'bg-blue-500';
      case 'paid': return 'bg-emerald-500';
      default: return 'bg-gray-500';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch(status) {
      case 'paid': return 'bg-green-500';
      case 'pending': return 'bg-yellow-500';
      case 'refunded': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const renderStars = (value, interactive = false) => {
    const displayRating = interactive ? (hoverRating || rating) : value;
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-6 w-6 ${interactive ? 'cursor-pointer' : ''} ${
              star <= displayRating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'
            }`}
            onClick={interactive ? () => setRating(star) : undefined}
            onMouseEnter={interactive ? () => setHoverRating(star) : undefined}
            onMouseLeave={interactive ? () => setHoverRating(0) : undefined}
          />
        ))}
        {value && !interactive && <span className="text-sm ml-2 text-gray-400">({value}/5)</span>}
      </div>
    );
  };

  const BookingCard = ({ booking, isEquipment = false }) => (
    <Card className="bg-gray-800 border-gray-700 hover:border-yellow-500 transition-all">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <CardTitle className="text-xl text-white mb-2">{booking?.event_name || 'N/A'}</CardTitle>
            <CardDescription className="text-gray-400">
              {isEquipment ? booking?.equipment_name : booking?.system_name}
              {booking?.quantity > 1 && <span className="ml-1">(x{booking.quantity})</span>}
            </CardDescription>
            {booking?.business_name && (
              <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                <Building2 className="h-4 w-4 text-yellow-500" />
                {booking.business_name}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 items-end">
            <div className="flex gap-2">
              <Badge className={`${getStatusColor(booking?.status)} text-white`}>
                {capitalize(booking?.status)}
              </Badge>
              <Badge className={`${getPaymentStatusColor(booking?.payment_status)} text-white`}>
                {capitalize(booking?.payment_status)}
              </Badge>
            </div>
            {(booking?.rating || booking?.business_rating) && (
              <div className="flex items-center gap-1">
                {renderStars(booking?.rating || booking?.business_rating)}
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="flex items-start space-x-3">
            <Calendar className="h-5 w-5 text-yellow-500 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">Date</p>
              <p className="font-medium text-white">{formatDateUK(booking?.event_date)}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Clock className="h-5 w-5 text-yellow-500 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">Time</p>
              <p className="font-medium text-white">{booking?.event_time || 'N/A'}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <MapPin className="h-5 w-5 text-yellow-500 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">Location</p>
              <p className="font-medium text-white truncate max-w-[150px]">{booking?.location || 'N/A'}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <DollarSign className="h-5 w-5 text-yellow-500 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">Amount</p>
              <p className="font-bold text-yellow-500">{formatCurrency(booking?.total_amount || 0)}</p>
            </div>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-700">
          <Button
            variant="outline"
            className="border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white"
            onClick={() => openDetailsModal({ ...booking, isEquipment })}
            data-testid={`view-details-${booking?.booking_id}`}
          >
            <Eye className="h-4 w-4 mr-2" />
            View Details
          </Button>
          {canPay(booking) && (
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => handlePayNow(booking, isEquipment)}
              disabled={paying}
              data-testid={`pay-now-${booking?.booking_id}`}
            >
              <CreditCard className="h-4 w-4 mr-2" />
              {paying ? 'Processing...' : 'Pay Now'}
            </Button>
          )}
          {canCancel(booking) && (
            <Button
              variant="outline"
              className="border-red-600 text-red-500 hover:bg-red-600 hover:text-white"
              onClick={() => handleCancelBooking(booking, isEquipment)}
              disabled={cancelling}
              data-testid={`cancel-booking-${booking?.booking_id}`}
            >
              <XCircle className="h-4 w-4 mr-2" />
              {cancelling ? 'Cancelling...' : 'Cancel Booking'}
            </Button>
          )}
          {canRate(booking) && (
            <Button
              variant="outline"
              className="border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black"
              onClick={() => openRatingModal(booking)}
              data-testid={`rate-service-${booking?.booking_id}`}
            >
              <Star className="h-4 w-4 mr-2" />
              Rate Service
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <Loader2 className="h-12 w-12 animate-spin text-yellow-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
            <Calendar className="h-10 w-10 text-yellow-500" />
            My Bookings
          </h1>
          <p className="text-lg text-gray-400">View and manage your bookings</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-8 bg-gray-800">
            <TabsTrigger value="systems" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <Calendar className="h-4 w-4 mr-2" />
              PA Systems ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="equipment" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <Package className="h-4 w-4 mr-2" />
              Equipment ({equipmentBookings.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="systems">
            {bookings.length === 0 ? (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="py-12 text-center">
                  <Calendar className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No PA System Bookings Yet</h3>
                  <p className="text-gray-400 mb-6">Start by browsing our PA systems</p>
                  <Button
                    className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
                    onClick={() => navigate('/browse')}
                  >
                    Browse PA Systems
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {bookings.map((booking) => (
                  <BookingCard key={booking.booking_id} booking={booking} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="equipment">
            {equipmentBookings.length === 0 ? (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="py-12 text-center">
                  <Package className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Equipment Bookings Yet</h3>
                  <p className="text-gray-400 mb-6">Browse individual equipment for rent</p>
                  <Button
                    className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
                    onClick={() => navigate('/equipment')}
                  >
                    Browse Equipment
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {equipmentBookings.map((booking) => (
                  <BookingCard key={booking.booking_id} booking={booking} isEquipment={true} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Rating Modal */}
      <Dialog open={showRatingModal} onOpenChange={setShowRatingModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Rate Your Experience</DialogTitle>
            <DialogDescription className="text-gray-400">
              How was your experience with this service?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="flex justify-center">
              {renderStars(rating, true)}
            </div>
            <div className="space-y-2">
              <Label htmlFor="comment" className="text-gray-300">Comment (optional)</Label>
              <Textarea
                id="comment"
                placeholder="Share your experience..."
                value={ratingComment}
                onChange={(e) => setRatingComment(e.target.value)}
                rows={4}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            {ratingError && (
              <p className="text-red-400 text-sm">{ratingError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRatingModal(false)} className="border-gray-600 text-gray-300">
              Cancel
            </Button>
            <Button
              onClick={handleSubmitRating}
              disabled={submittingRating || rating === 0}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              {submittingRating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit Rating'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">Booking Details</DialogTitle>
            <DialogDescription className="text-gray-400">
              Booking ID: {selectedBooking?.booking_id}
            </DialogDescription>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4 py-4">
              {/* Status Badges */}
              <div className="flex gap-2">
                <Badge className={`${getStatusColor(selectedBooking?.status)} text-white`}>
                  {capitalize(selectedBooking?.status)}
                </Badge>
                <Badge className={`${getPaymentStatusColor(selectedBooking?.payment_status)} text-white`}>
                  Payment: {capitalize(selectedBooking?.payment_status)}
                </Badge>
              </div>

              {/* Event Info */}
              <div className="bg-gray-700 rounded-lg p-4 space-y-3">
                <h4 className="font-semibold text-yellow-500">{selectedBooking?.event_name}</h4>
                <p className="text-sm text-gray-300">
                  {selectedBooking?.isEquipment ? selectedBooking?.equipment_name : selectedBooking?.system_name}
                  {selectedBooking?.quantity > 1 && ` (x${selectedBooking.quantity})`}
                </p>
                {selectedBooking?.business_name && (
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Building2 className="h-4 w-4 text-yellow-500" />
                    {selectedBooking.business_name}
                  </div>
                )}
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-start gap-2">
                  <Calendar className="h-4 w-4 text-yellow-500 mt-1" />
                  <div>
                    <p className="text-xs text-gray-500">Date</p>
                    <p className="text-sm text-white">{formatDateUK(selectedBooking?.event_date)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Clock className="h-4 w-4 text-yellow-500 mt-1" />
                  <div>
                    <p className="text-xs text-gray-500">Time</p>
                    <p className="text-sm text-white">{selectedBooking?.event_time}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-yellow-500 mt-1" />
                  <div>
                    <p className="text-xs text-gray-500">Location</p>
                    <p className="text-sm text-white">{selectedBooking?.location || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Users className="h-4 w-4 text-yellow-500 mt-1" />
                  <div>
                    <p className="text-xs text-gray-500">Attendees</p>
                    <p className="text-sm text-white">{selectedBooking?.attendees || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Phone className="h-4 w-4 text-yellow-500 mt-1" />
                  <div>
                    <p className="text-xs text-gray-500">Contact</p>
                    <p className="text-sm text-white">{selectedBooking?.contact_phone || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Clock className="h-4 w-4 text-yellow-500 mt-1" />
                  <div>
                    <p className="text-xs text-gray-500">Duration</p>
                    <p className="text-sm text-white">{selectedBooking?.duration || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Special Requirements */}
              {selectedBooking?.special_requirements && (
                <div className="bg-gray-700 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Special Requirements</p>
                  <p className="text-sm text-white">{selectedBooking.special_requirements}</p>
                </div>
              )}

              {/* Pricing */}
              <div className="bg-gray-700 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Total Amount</span>
                  <span className="text-2xl font-bold text-yellow-500">
                    {formatCurrency(selectedBooking?.total_amount || 0)}
                  </span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex gap-2">
            {canPay(selectedBooking) && (
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => handlePayNow(selectedBooking, selectedBooking?.isEquipment)}
                disabled={paying}
              >
                <CreditCard className="h-4 w-4 mr-2" />
                {paying ? 'Processing...' : 'Pay Now'}
              </Button>
            )}
            {canCancel(selectedBooking) && (
              <Button
                variant="outline"
                className="border-red-600 text-red-500 hover:bg-red-600 hover:text-white"
                onClick={() => handleCancelBooking(selectedBooking, selectedBooking?.isEquipment)}
                disabled={cancelling}
              >
                <XCircle className="h-4 w-4 mr-2" />
                {cancelling ? 'Cancelling...' : 'Cancel Booking'}
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={() => setShowDetailsModal(false)} 
              className="border-gray-600 text-gray-300"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyBookings;
